/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * PasswordInput Component - Password field with strength meter
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import React, { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import PropTypes from 'prop-types'
import { Eye, EyeOff, AlertCircle, CheckCircle2, Lock } from 'lucide-react'

/**
 * Calculate password strength
 * @param {string} password
 * @returns {Object} strength info
 */
const calculatePasswordStrength = (password) => {
  if (!password) return { score: 0, label: '', color: 'gray' }

  let score = 0
  const checks = {
    length: password.length >= 8,
    lowercase: /[a-z]/.test(password),
    uppercase: /[A-Z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[^A-Za-z0-9]/.test(password)
  }

  // Calculate score
  if (checks.length) score += 20
  if (password.length >= 12) score += 10
  if (checks.lowercase) score += 20
  if (checks.uppercase) score += 20
  if (checks.number) score += 15
  if (checks.special) score += 15

  // Determine strength level
  let label = ''
  let color = ''

  if (score < 40) {
    label = 'Weak'
    color = 'error'
  } else if (score < 60) {
    label = 'Fair'
    color = 'warning'
  } else if (score < 80) {
    label = 'Good'
    color = 'primary'
  } else {
    label = 'Strong'
    color = 'success'
  }

  return { score, label, color, checks }
}

/**
 * PasswordInput Component
 *
 * Enhanced password input with:
 * - Show/hide toggle
 * - Real-time strength meter
 * - Strength indicators
 * - Visual progress bar
 *
 * @param {Object} props
 */
const PasswordInput = ({
  id,
  name,
  label,
  value,
  onChange,
  onBlur,
  error,
  required,
  disabled,
  autoComplete,
  showStrengthMeter = false,
  ...rest
}) => {
  const [showPassword, setShowPassword] = useState(false)
  const [isFocused, setIsFocused] = useState(false)

  const strength = useMemo(() => calculatePasswordStrength(value), [value])
  const hasValue = Boolean(value && value.length > 0)
  const isFloating = isFocused || hasValue

  return (
    <div className="relative">
      {/* Input Container */}
      <div className="relative">
        {/* Lock Icon */}
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10">
          <Lock className="w-5 h-5" />
        </div>

        {/* Input Field */}
        <input
          id={id}
          name={name}
          type={showPassword ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          onBlur={(e) => {
            setIsFocused(false)
            onBlur && onBlur(e)
          }}
          onFocus={() => setIsFocused(true)}
          disabled={disabled}
          required={required}
          autoComplete={autoComplete}
          placeholder=""
          className={`
                        w-full px-3 py-2.5 pl-10 pr-10
                        bg-white dark:bg-gray-800
                        border rounded-lg
                        text-sm text-gray-900 dark:text-white
                        placeholder-gray-400 dark:placeholder-gray-500
                        transition-all duration-200
                        focus:outline-none focus:ring-2 focus:ring-offset-0
                        disabled:bg-gray-100 dark:disabled:bg-gray-900 disabled:cursor-not-allowed
                        ${
                          error
                            ? 'border-error-300 focus:border-error-500 focus:ring-error-500/20'
                            : 'border-gray-300 dark:border-gray-600 focus:border-primary-500 focus:ring-primary-500/20 hover:border-gray-400 dark:hover:border-gray-500'
                        }
                    `}
          {...rest}
        />

        {/* Floating Label */}
        <motion.label
          htmlFor={id}
          initial={false}
          animate={{
            top: isFloating ? '-0.5rem' : '50%',
            y: isFloating ? 0 : '-50%',
            fontSize: isFloating ? '0.75rem' : '0.875rem',
            left: isFloating ? '0.75rem' : '2.5rem'
          }}
          transition={{ duration: 0.2 }}
          className={`
                        absolute px-1
                        bg-white dark:bg-gray-800
                        pointer-events-none
                        transition-colors duration-200
                        ${
                          error
                            ? 'text-error-600'
                            : isFocused
                              ? 'text-primary-600'
                              : 'text-gray-500 dark:text-gray-400'
                        }
                    `}
        >
          {label}
          {required && <span className="text-error-500 ml-1">*</span>}
        </motion.label>

        {/* Show/Hide Toggle & Status Icon */}
        <div className="absolute right-2.5 top-1/2 -translate-y-1/2 flex items-center space-x-1.5">
          <AnimatePresence mode="wait">
            {error && (
              <motion.div
                key="error"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.2 }}
              >
                <AlertCircle className="w-4 h-4 text-error-500" />
              </motion.div>
            )}
            {!error && showStrengthMeter && strength.score >= 80 && hasValue && (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.2 }}
              >
                <CheckCircle2 className="w-4 h-4 text-success-500" />
              </motion.div>
            )}
          </AnimatePresence>

          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-0.5"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Password Strength Meter */}
      {showStrengthMeter && hasValue && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="mt-3 space-y-2"
        >
          {/* Progress Bar */}
          <div className="flex items-center space-x-2">
            <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${strength.score}%` }}
                transition={{ duration: 0.3 }}
                className={`h-full rounded-full transition-colors ${
                  strength.color === 'error'
                    ? 'bg-error-500'
                    : strength.color === 'warning'
                      ? 'bg-warning-500'
                      : strength.color === 'primary'
                        ? 'bg-primary-500'
                        : 'bg-success-500'
                }`}
              />
            </div>
            <span
              className={`text-sm font-medium ${
                strength.color === 'error'
                  ? 'text-error-600'
                  : strength.color === 'warning'
                    ? 'text-warning-600'
                    : strength.color === 'primary'
                      ? 'text-primary-600'
                      : 'text-success-600'
              }`}
            >
              {strength.label}
            </span>
          </div>

          {/* Requirements Checklist */}
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div
              className={`flex items-center ${strength.checks?.length ? 'text-success-600' : 'text-gray-400'}`}
            >
              <CheckCircle2
                className={`w-3 h-3 mr-1 ${strength.checks?.length ? '' : 'opacity-30'}`}
              />
              <span>8+ characters</span>
            </div>
            <div
              className={`flex items-center ${strength.checks?.uppercase ? 'text-success-600' : 'text-gray-400'}`}
            >
              <CheckCircle2
                className={`w-3 h-3 mr-1 ${strength.checks?.uppercase ? '' : 'opacity-30'}`}
              />
              <span>Uppercase letter</span>
            </div>
            <div
              className={`flex items-center ${strength.checks?.lowercase ? 'text-success-600' : 'text-gray-400'}`}
            >
              <CheckCircle2
                className={`w-3 h-3 mr-1 ${strength.checks?.lowercase ? '' : 'opacity-30'}`}
              />
              <span>Lowercase letter</span>
            </div>
            <div
              className={`flex items-center ${strength.checks?.number ? 'text-success-600' : 'text-gray-400'}`}
            >
              <CheckCircle2
                className={`w-3 h-3 mr-1 ${strength.checks?.number ? '' : 'opacity-30'}`}
              />
              <span>Number</span>
            </div>
          </div>
        </motion.div>
      )}

      {/* Error Message */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0, y: -10 }}
            animate={{ opacity: 1, height: 'auto', y: 0 }}
            exit={{ opacity: 0, height: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <p className="text-error-600 text-sm mt-2 flex items-start">
              <AlertCircle className="w-4 h-4 mr-1 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

PasswordInput.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  error: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  autoComplete: PropTypes.string,
  showStrengthMeter: PropTypes.bool
}

export default PasswordInput
